import { breachCostByCountry, breachCostByIndustry } from '@sailpoint/bva-core';

// --- Default Values --- //
const DEFAULT_TOTAL_USERS = 5000;

export const defaultInputs = {
    // General Information
    total_users: DEFAULT_TOTAL_USERS,
    average_annual_salary: 75000,
    average_manager_salary: 100000,
    average_it_helpdesk_salary: 130000,
    salary_burden_pc: 30,
    location: 'United States', 
    industry: 'Energy',
    currency_iso_code: 'USD',
    currency_symbol: '$',
    desired_benefits: [],

    // Benefit Realization %
    year_1_realized_benefit: 50,
    year_2_realized_benefit: 75,
    year_3_realized_benefit: 100,
    year_4_realized_benefit: 100,
    year_5_realized_benefit: 100,

    // ---- Detailed Assumptions
    // Optimize SaaS Management
    avg_saas_apps: 5,
    avg_saas_app_cost: 240,

    // Oversee Multi-Cloud Environments
    no_cloud_admins: 1,
    number_of_public_cloud_environments: 2,

    // Manage ERP Access Risk
    input_erp_access: 160, // Hours
    input_erp_resources: 1, // FTEs

    // Automate Lifecycle Events
    pc_joiners: 15,
    pc_movers: 17,
    pc_leavers: 15,
    current_level_of_jml_automation: 'Fairly automated (30 minutes)',

    // Optimize User Productivity
    user_productivity: 'A moderate amount - users usually gain access the same day',

    // Expedite Ad Hoc Requests
    annual_adhoc_requests_per_user: 3,
    current_level_of_adhoc_access_request_automation: 'Fairly automated (30 minutes)',

    // Reduce Password Resets
    annual_password_resets_per_user: 2,

    // Improve Internal Reviews
    number_of_compliance_analysts: 2,
    level_of_manager_reviewer_burden_during_access_certification: 'Very little - not performed at all or rubber stamped without proper review (30 minutes)',
    
    // Avoid Audit Findings
    average_cost_per_audit_finding: 10000,
    annual_audit_findings: 3,

    // Replace Legacy Systems
    annual_legacy_systems_costs: 100000,

    // Accelerate Application Onboarding
    applications_to_be_onboarded: 20,

    // Streamline Role-Based Access
    number_of_ftes: 1, // Role-Based Access FTEs
    maturity_level_of_RBAC_system: 'Very immature (10%)',

    // Minimize Breach Costs
    annual_breach_likelihood_pre_sailpoint: 20,
    annual_breach_likelihood_post_sailpoint: 5,

    // Govern Unstructured Data
    input_unstructured_data: 500, // Hours per month

    // Machine Identity Security
    machine_accounts_in_focus: DEFAULT_TOTAL_USERS, // 1x of total users
    machine_account_govern_time_mins: 10,
    machine_account_requests_per_year: DEFAULT_TOTAL_USERS * 0.1, // 0.1x of total users
    machine_account_review_time_mins: 10,

    // Agent Identity Security
    agent_identities_in_focus: DEFAULT_TOTAL_USERS * 1, // 1x of total users
    agent_identity_govern_time_mins: 15,
    agent_identity_change_requests_per_year: DEFAULT_TOTAL_USERS * 0.1, // 0.1x of total users
    agent_identity_new_agents_per_year: DEFAULT_TOTAL_USERS * 0.1, // 0.1x of total users
    agent_identity_requests_per_year: DEFAULT_TOTAL_USERS * 0.1, // 0.1x of total users,
    agent_identity_review_time_mins: 15,
    agent_identities_revenue_generating_pc: 10, // percentage
};

// --- Stores --- //
export let bvaInputs = defaultInputs;
export const calculationResults = null;
export const currencyRate = { currency_iso_code: 'USD', currency_conversion_rate: 1.0 };

// --- UI State Stores --- //
export const isCalculating = false;
export const calculationError = null;
export const logErrorMessage = null;

// --- Form Select Options (from @sailpoint/bva-core) --- //
export const locationOptions = Object.keys(breachCostByCountry);
export const industryOptions = Object.keys(breachCostByIndustry);

export const currencyOptions = [
    { isoCode: 'USD', symbol: '$', name: 'USD - United States Dollar' },
    { isoCode: 'EUR', symbol: '€', name: 'EUR - Euro' },
    { isoCode: 'GBP', symbol: '£', name: 'GBP - British Pound' },
    { isoCode: 'CAD', symbol: '$', name: 'CAD - Canadian Dollar' },
    { isoCode: 'AUD', symbol: '$', name: 'AUD - Australian Dollar' },
    { isoCode: 'JPY', symbol: '¥', name: 'JPY - Japanese Yen' },
    { isoCode: 'NZD', symbol: '$', name: 'NZD - New Zealand Dollar' },
    { isoCode: 'SEK', symbol: 'kr', name: 'SEK - Swedish Krona' },
    { isoCode: 'CHF', symbol: 'Fr', name: 'CHF - Swiss Franc' },
    { isoCode: 'SGD', symbol: '$', name: 'SGD - Singapore Dollar' },
    { isoCode: 'BRL', symbol: 'R$', name: 'BRL - Brazilian Real' }
];

export const jmlAutomationOptions = ['Fairly automated (30 minutes)', 'Moderate level of manual effort (45 minutes)', 'Significant manual effort (120 minutes)'];
export const adhocAutomationOptions = jmlAutomationOptions;
export const userProductivityOptions = ['Very little - Users usually gain access within a few hours', 'A moderate amount - users usually gain access the same day', 'Significant delays - access can take several days'];
export const managerReviewerBurdenOptions = ['Very little - not performed at all or rubber stamped without proper review (30 minutes)', 'Moderate - Managers/reviewers spend an appropriate amount of time reviewing access (60 minutes)', 'Significant - reviewers are overburdened and spend excessive time (120 minutes)'];
export const rbacMaturityOptions = ['Very immature (10%)', "We've made a moderate amount of progress building a role system (50%)",'Highly mature system deployed (75%)'];

// --- Helper Functions --- //

// Currency conversion
const FX_API_BASE = 'https://fxapi.app/api';
const FX_CACHE_PREFIX = 'fx_rate_';

function getCachedRate(currency) {
    try {
        const raw = sessionStorage.getItem(FX_CACHE_PREFIX + currency);
        if (!raw) return null;
        const cached = JSON.parse(raw);
        if (typeof cached.rate !== 'number' || cached.rate <= 0) return null;
        return cached.rate;
    } catch {
        return null;
    }
}

function setCachedRate(currency, rate) {
    try {
        sessionStorage.setItem(FX_CACHE_PREFIX + currency, JSON.stringify({ rate }));
    } catch {
        // sessionStorage unavailable (e.g. private browsing quota exceeded)
    }
}

/**
 * Fetches the USD -> targetCurrency exchange rate from fxapi.app.
 * Caches rates in sessionStorage to avoid redundant API calls within a session.
 * Returns the numeric rate. Throws on failure so the caller can handle UI state.
 * @param {string} targetCurrency ISO 4217 code (e.g., 'EUR', 'BRL')
 * @returns {Promise<number>}
 */
export async function fetchExchangeRate(targetCurrency) {
    if (!targetCurrency || targetCurrency === 'USD') {
        return 1.0;
    }

    const cached = getCachedRate(targetCurrency);
    if (cached !== null) {
        return cached;
    }

    const url = `${FX_API_BASE}/USD/${encodeURIComponent(targetCurrency)}.json`;
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Exchange rate API returned ${response.status}`);
    }

    const data = await response.json();
    const rate = data?.rate;

    if (typeof rate !== 'number' || rate <= 0) {
        throw new Error(`Invalid exchange rate received for ${targetCurrency}`);
    }

    setCachedRate(targetCurrency, rate);
    return rate;
}